<script setup lang="ts">
    import { SidebarProvider, SidebarInset } from '@/shared/ui/Sidebar';
    import { useUserStore } from '@/stores/user.store';
    import { Layout } from './layout';
    import AppSidebar from './components/AppSidebar/AppSidebar.vue';
    import AppHeader from './components/AppHeader/AppHeader.vue';
    import AppCommandPalette from './components/AppCommandPalette/AppCommandPalette.vue';

    const { defaultLayout } = defineProps<{ defaultLayout: Layout }>();

    const router = useRouter();
    const layout = ref<Layout>(defaultLayout);

    const userStore = useUserStore();
    const accountType = computed(() => userStore.account?.type);

    const sidebarVisible = computed(() => {
        return !!userStore.user && layout.value === Layout.Sidebar;
    });

    const headerVisible = computed(() => {
        return layout.value !== Layout.Blank;
    });

    router.beforeEach((location) => {
        const selectedLayout = location.matched
            .map((l) => l.meta.layout as Layout | undefined)
            .filter(Boolean)
            .toReversed() // last one wins
            .reduce((acc, current) => acc ?? current, undefined as Layout | undefined);

        layout.value = selectedLayout ?? defaultLayout;
    });

    const mainContentClass = computed(() => {
        if (layout.value === Layout.Blank) return {};

        return {
            'p-[24px]': true,
            // 1784px of content, as before the navigation rework, plus the 2 × 24px padding
            'max-w-458': accountType.value && accountType.value !== 'client',
            'max-w-295': !accountType.value || accountType.value === 'client',
        };
    });
</script>

<template>
    <SidebarProvider class="h-screen w-screen">
        <AppSidebar v-if="sidebarVisible" />
        <SidebarInset class="min-h-screen flex flex-col">
            <AppHeader
                v-if="headerVisible"
                :sidebar-enabled="sidebarVisible"
            />
            <div class="relative flex-1 overflow-auto">
                <div
                    class="w-full mx-auto"
                    :class="mainContentClass"
                >
                    <slot />
                </div>
            </div>
        </SidebarInset>
        <AppCommandPalette v-if="userStore.isSupport" />
    </SidebarProvider>
</template>
